import dayjs from 'dayjs';
import { DATE_ISO8601_FORMAT, ZERO_TIME } from '@/constants';

/**
 * Project a sensor's next battery change date from its last change date and
 * estimated runtime.
 *
 * Returns `null` when input is invalid:
 *   - missing/empty/sentinel `lastDate`
 *   - missing, non-finite (NaN/Infinity), or non-positive `runtimeDays`
 *   - unparseable `lastDate`
 *
 * Returning `null` rather than the ZERO_TIME sentinel keeps the "no value"
 * marker out of the in-memory contract — callers can `if (!nextDate)` instead
 * of remembering the sentinel-comparison. The wire format still uses
 * ZERO_TIME for stored values; this helper produces in-memory projections,
 * not on-the-wire payloads. A previous version returned `lastDate` itself for
 * `runtimeDays = 0`, which surfaced as a same-day "next change" — semantically
 * wrong, and silently dominated the floor-level earliest-projection sort.
 * Current contract rejects 0/negative/non-finite runtime for that reason.
 */
export function computeNextBatteryChangeDate(
  lastDate: string,
  runtimeDays?: number | null,
): string | null {
  if (
    !lastDate ||
    lastDate === ZERO_TIME ||
    runtimeDays == null ||
    !Number.isFinite(runtimeDays) ||
    runtimeDays <= 0
  ) {
    return null;
  }
  const parsed = dayjs(lastDate, DATE_ISO8601_FORMAT);
  if (!parsed.isValid()) return null;
  return parsed.add(runtimeDays, 'day').format(DATE_ISO8601_FORMAT);
}

export const BATTERY_COLORS = {
  GREY: '#8F91A3',
  BLUE: '#3B82F6',
  GREEN: '#10B981',
  ORANGE: '#EB860A',
  RED: '#FF0000',
} as const;

const THIRTY_DAYS = 30;

export function getBatteryStatusColor(sensor: {
  powerType: 'Wired' | 'Battery';
  lastBatteryChangeDate?: string | null;
  estimatedTotalRuntimeDays?: number | null;
}): string | null {
  if (sensor.powerType !== 'Battery') return null;

  const lastChange = sensor.lastBatteryChangeDate;
  if (!lastChange || lastChange === ZERO_TIME) return BATTERY_COLORS.GREY;

  const lastChangeDate = dayjs(lastChange, DATE_ISO8601_FORMAT);
  if (!lastChangeDate.isValid()) return BATTERY_COLORS.GREY;

  const now = dayjs();

  const runtimeDays = sensor.estimatedTotalRuntimeDays;
  if (runtimeDays != null) {
    const nextChangeDate = lastChangeDate.add(runtimeDays, 'day');
    const daysUntilNext = nextChangeDate.diff(now, 'day');

    // Overdue/due-soon take priority over "recently changed"
    if (daysUntilNext <= 0) return BATTERY_COLORS.RED;
    if (daysUntilNext < THIRTY_DAYS) return BATTERY_COLORS.ORANGE;
  }

  const daysSinceChange = now.diff(lastChangeDate, 'day');
  if (daysSinceChange <= THIRTY_DAYS) return BATTERY_COLORS.BLUE;
  if (runtimeDays == null) return BATTERY_COLORS.GREY;
  return BATTERY_COLORS.GREEN;
}
